import {
  ResponsiveContainer, ComposedChart, LineChart, Bar, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ReferenceLine,
} from "recharts";

const SERIES_COLORS = ["#07005D", "#0BADFF", "#F2005E", "#12B76A"];

const fmtSpend = (v) => (v == null ? "" : v >= 1e6 ? `$${+(v / 1e6).toFixed(2)}M` : v >= 1000 ? `$${+(v / 1000).toFixed(1)}k` : `$${v}`);
const fmtRoas = (v) => (v == null ? "" : `${Number(v).toFixed(2)}x`);

function ChartTable({ chart }) {
  const roasKeys =
    chart.type === "spendRoas"
      ? chart.series.map((s) => `ROAS_${s}`).filter((k) => chart.data.some((d) => d[k] != null))
      : [];
  const cols =
    chart.type === "spendRoas" ? [...chart.series, ...roasKeys]
    : chart.type === "revenue" ? [...chart.series, "Total"]
    : ["share"];
  const fmt = (key, v) => {
    if (v == null) return "\u2013";
    if (key === "share") return `${v}%`;
    if (key.startsWith("ROAS_")) return fmtRoas(v);
    return fmtSpend(v);
  };
  const cell = (d, c) => (c === "Total" ? chart.series.reduce((a, s) => a + (d[s] || 0), 0) : d[c]);
  const label = (key) =>
    key === "share" ? "Share"
    : key.startsWith("ROAS_") ? `${key.slice(5)} ROAS`
    : chart.type === "revenue" ? (key === "Total" ? "Total revenue" : `${key} revenue`)
    : `${key} spend`;

  return (
    <div className="mt-6 overflow-x-auto">
      <table className="w-full min-w-[480px] text-left text-sm" data-testid="chart-data-table">
        <thead>
          <tr className="border-b border-mist text-xs font-bold uppercase tracking-widest text-navy/50">
            <th className="pb-2 pr-4">Month</th>
            {cols.map((c) => <th key={c} className="pb-2 pr-4">{label(c)}</th>)}
          </tr>
        </thead>
        <tbody>
          {chart.data.map((d) => (
            <tr key={d.month} className="border-b border-mist/50 last:border-0">
              <td className="py-2.5 pr-4 font-bold text-navy">{d.month}</td>
              {cols.map((c) => <td key={c} className="py-2.5 pr-4 text-navy/70">{fmt(c, cell(d, c))}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function CaseChart({ chart, compact = false }) {
  return (
    <div data-testid="case-chart" className={compact ? "rounded-xl border border-mist bg-white p-3" : "rounded-xl border border-mist bg-white p-5 md:p-8"}>
      {!compact && <h3 className="text-xl font-bold text-navy md:text-2xl">{chart.title}</h3>}
      <div className={compact ? "h-44 w-full" : "mt-6 h-72 w-full md:h-96"}>
        <ResponsiveContainer width="100%" height="100%">
          {chart.type === "share" ? (
            <LineChart data={chart.data} margin={{ top: 10, right: 10, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E6E6F0" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#07005D" }} />
              <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11, fill: "#07005D" }} />
              <Tooltip formatter={(v) => [`${v}%`, "Share"]} />
              <Line type="monotone" dataKey="share" stroke="#F2005E" strokeWidth={3} dot={{ r: 5, fill: "#F2005E" }} name="Share of spend" />
            </LineChart>
          ) : chart.type === "revenue" ? (
            <ComposedChart data={chart.data} margin={{ top: 10, right: 0, left: -6, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E6E6F0" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#07005D" }} interval={0} />
              <YAxis tickFormatter={fmtSpend} tick={{ fontSize: 11, fill: "#07005D" }} />
              <Tooltip formatter={(v, name) => [fmtSpend(v), `${name} revenue`]} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {chart.series.map((s, i) => (
                <Bar key={s} dataKey={s} stackId="rev" fill={SERIES_COLORS[i % SERIES_COLORS.length]} name={s} radius={i === chart.series.length - 1 ? [4, 4, 0, 0] : 0} />
              ))}
              {chart.annotation && (
                <ReferenceLine
                  x={chart.annotation.month}
                  stroke="#F2005E"
                  strokeDasharray="4 4"
                  label={{ value: chart.annotation.label, position: "insideTopLeft", fontSize: 10, fill: "#F2005E" }}
                />
              )}
            </ComposedChart>
          ) : (
            <ComposedChart data={chart.data} margin={{ top: 10, right: 0, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E6E6F0" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#07005D" }} />
              <YAxis yAxisId="left" tickFormatter={fmtSpend} tick={{ fontSize: 11, fill: "#07005D" }} />
              <YAxis yAxisId="right" orientation="right" domain={[0, 2]} tickFormatter={(v) => `${v}x`} tick={{ fontSize: 11, fill: "#12B76A" }} />
              <Tooltip formatter={(v, name) => (name.startsWith("ROAS") ? [fmtRoas(v), name.replace("ROAS_", "") + " ROAS"] : [fmtSpend(v), name + " spend"])} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {chart.series.map((s, i) => (
                <Bar key={s} yAxisId="left" dataKey={s} stackId="spend" fill={SERIES_COLORS[i % SERIES_COLORS.length]} name={s} />
              ))}
              <Line yAxisId="right" type="monotone" dataKey="ROAS_Meta" stroke="#12B76A" strokeWidth={3} dot={{ r: 4 }} name="ROAS_Meta" />
              {chart.annotation && (
                <ReferenceLine
                  yAxisId="left"
                  x={chart.annotation.month}
                  stroke="#F2005E"
                  strokeDasharray="4 4"
                  label={{ value: chart.annotation.label, position: "insideTopLeft", fontSize: 10, fill: "#F2005E" }}
                />
              )}
            </ComposedChart>
          )}
        </ResponsiveContainer>
      </div>
      {!compact && chart.note && <p className="mt-4 text-sm italic text-navy/60">{chart.note}</p>}
      {!compact && chart.stats && (
        <div className="mt-6 grid grid-cols-2 gap-6 border-t border-mist pt-6 md:grid-cols-4">
          {chart.stats.map((s) => (
            <div key={s.label}>
              <div className="text-2xl font-extrabold text-navy">{s.value}</div>
              <div className="mt-1 text-xs font-medium uppercase tracking-widest text-navy/60">{s.label}</div>
            </div>
          ))}
        </div>
      )}
      {!compact && <ChartTable chart={chart} />}
    </div>
  );
}
